import * as Icons from 'lucide-react';
import { ArrowRight, CheckCircle, Mail, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { resolveMediaUrl } from '../lib/api';

/**
 * Vistas previas del sitio público de Pizas. Reflejan las secciones de `HomePage`
 * y usan las clases de tema (`bg-primary`, `text-primary`, …) para que los colores
 * se actualicen en vivo conforme el super admin edita. Todo es de solo lectura.
 */

const DEFAULT_BRAND_NAME = 'Pizzas';
const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=1200&q=80';
const DEFAULT_CLINIC_IMAGE = 'https://images.unsplash.com/photo-1574071318508-1c0fd373779b?w=800&q=80';

export const DEFAULT_HOME_FEATURES = [
  { icon: 'heart', title: 'Ingredientes frescos', desc: 'Masa y toppings de calidad' },
  { icon: 'clock', title: 'Entrega a tiempo', desc: 'Te avisamos cuando va en camino' },
  { icon: 'sparkles', title: 'Especialidades', desc: 'Recetas con nombre propio' },
  { icon: 'star', title: 'Pide fácil', desc: 'Web o WhatsApp' },
];

const DEFAULT_SERVICES_BULLETS = [
  'Masa artesanal',
  'Ingredientes frescos',
  'Especialidades de la casa',
  'Entrega a domicilio',
  'Pide por WhatsApp',
  'Pago en línea o transferencia',
];

function txt(value, fallback) {
  const v = String(value ?? '').trim();
  return v || fallback;
}

function iconFromApiName(name) {
  const pascal = String(name || 'shield')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');
  return Icons[pascal] || Icons.Shield;
}

function resolveImg(url, fallback) {
  if (typeof url === 'string' && url.trim()) return resolveMediaUrl(url) || fallback;
  return fallback;
}

export function PreviewFrame({ label, children }) {
  return (
    <div
      className="rounded-2xl border border-border overflow-hidden bg-background shadow-sm"
      data-testid="site-preview-frame"
    >
      <div className="px-4 py-2 bg-muted/60 border-b border-border text-xs font-medium text-muted-foreground">
        {label || 'Vista previa'}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function NavPreview({
  name,
  logoUrl,
  phone,
  email,
  siteTaglineShort,
  address,
  navHome,
  navMenu,
  navReviews,
  navContact,
  ctaButtonLabel,
}) {
  const brand = txt(name, DEFAULT_BRAND_NAME);
  const logo = resolveImg(logoUrl, '');
  const tagline = txt(siteTaglineShort, txt(address, 'Tu ciudad'));
  return (
    <PreviewFrame label="Vista previa — barra superior y navegación">
      <div className="rounded-lg overflow-hidden border border-border">
        <div className="bg-primary text-primary-foreground px-3 py-2 flex flex-wrap justify-between gap-2 text-xs">
          <div className="flex gap-3 flex-wrap">
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {txt(phone, '+52 …')}
            </span>
            <span className="hidden sm:flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {txt(email, 'correo@…')}
            </span>
          </div>
          <span className="flex items-center gap-1 max-w-[12rem] truncate">
            <MapPin className="w-3 h-3 shrink-0" />
            {tagline}
          </span>
        </div>
        <div className="bg-white px-3 py-3 flex items-center gap-3 border-b border-border">
          {logo ? (
            <img src={logo} alt={brand} className="h-8 w-auto max-w-[120px] object-contain" />
          ) : (
            <span className="font-heading font-bold text-primary">🍕 {brand}</span>
          )}
          <nav className="ml-auto flex items-center gap-3 text-xs text-foreground">
            <span>{txt(navHome, 'Inicio')}</span>
            <span>{txt(navMenu, 'Menú')}</span>
            <span className="hidden sm:inline">{txt(navReviews, 'Reseñas')}</span>
            <span className="hidden sm:inline">{txt(navContact, 'Contacto')}</span>
            <span className="rounded-full bg-primary text-primary-foreground px-3 py-1 font-medium">
              {txt(ctaButtonLabel, 'Pide tu pizza')}
            </span>
          </nav>
        </div>
      </div>
    </PreviewFrame>
  );
}

export function BrandingPreview({
  name,
  logoUrl,
  phone,
  email,
  address,
  footerBlurb,
  copyrightName,
}) {
  const brand = txt(name, DEFAULT_BRAND_NAME);
  const logo = resolveImg(logoUrl, '');
  const blurb = txt(footerBlurb, 'Pizza recién hecha con ingredientes frescos.');
  const copy = txt(copyrightName, brand);
  return (
    <PreviewFrame label="Vista previa — pie de página">
      <div className="rounded-lg overflow-hidden bg-primary text-primary-foreground p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div>
            {logo ? (
              <img
                src={logo}
                alt={brand}
                className="h-9 w-auto max-w-[110px] object-contain mb-2 brightness-0 invert"
              />
            ) : (
              <p className="font-heading font-bold mb-2">🍕 {brand}</p>
            )}
            <p className="text-primary-foreground/80 leading-relaxed">{blurb}</p>
          </div>
          <div>
            <p className="font-semibold mb-2">Horarios</p>
            <p className="text-primary-foreground/80 italic">
              Se generan automáticamente desde el menú «Horarios».
            </p>
          </div>
          <div>
            <p className="font-semibold mb-2">Contacto</p>
            <p className="text-primary-foreground/80">{txt(phone, '—')}</p>
            <p className="text-primary-foreground/80">{txt(email, '—')}</p>
            <p className="text-primary-foreground/80 mt-1">{txt(address, '—')}</p>
          </div>
        </div>
        <p className="text-center text-primary-foreground/60 text-[10px] mt-4 pt-3 border-t border-primary-foreground/20">
          © {new Date().getFullYear()} {copy}. Todos los derechos reservados.
        </p>
      </div>
    </PreviewFrame>
  );
}

export function ThemePreview({
  name,
  ctaButtonLabel,
  primaryHex,
  backgroundHex,
  foregroundHex,
  showCheckerboard,
}) {
  const brand = txt(name, DEFAULT_BRAND_NAME);
  const primary = (primaryHex || '#C62828').replace(/^([^#])/, '#$1');
  return (
    <PreviewFrame label="Vista previa — tema y colores">
      <div
        className="rounded-xl p-5 border border-border"
        style={{
          backgroundColor: backgroundHex || '#FFF5F5',
          color: foregroundHex || '#2D0A0A',
          backgroundImage: showCheckerboard
            ? `repeating-conic-gradient(${primary}14 0% 25%, transparent 0% 50%)`
            : undefined,
          backgroundSize: showCheckerboard ? '28px 28px' : undefined,
        }}
      >
        <p className="text-[10px] uppercase tracking-wide opacity-70 mb-2">Muestra de marca</p>
        <p className="font-heading text-2xl font-bold" style={{ color: primary }}>
          {brand}
        </p>
        <p className="text-sm opacity-80 mt-1">Así se ven los colores en el sitio.</p>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            tabIndex={-1}
            className="rounded-full px-5 py-2 text-sm font-medium text-white pointer-events-none"
            style={{ backgroundColor: primary }}
          >
            {txt(ctaButtonLabel, 'Botón principal')}
          </button>
          <button
            type="button"
            tabIndex={-1}
            className="rounded-full px-5 py-2 text-sm font-medium border pointer-events-none"
            style={{ borderColor: primary, color: primary }}
          >
            Secundario
          </button>
        </div>
      </div>
    </PreviewFrame>
  );
}

export function HeroPreview({
  brandName,
  badge,
  titlePrefix,
  titleHighlight,
  subtitle,
  heroImageUrl,
  ctaPrimaryLabel,
  ctaSecondaryLabel,
  showCheckerboard,
}) {
  const brand = txt(brandName, DEFAULT_BRAND_NAME);
  const heroImg = resolveImg(heroImageUrl, DEFAULT_HERO_IMAGE);
  return (
    <PreviewFrame label="Vista previa — hero (inicio)">
      <div
        className={`rounded-xl overflow-hidden p-4 ${showCheckerboard ? '' : 'bg-gradient-to-br from-muted/40 to-background'}`}
        style={
          showCheckerboard
            ? {
                backgroundImage:
                  'repeating-conic-gradient(rgba(198,40,40,0.08) 0% 25%, transparent 0% 50%)',
                backgroundSize: '28px 28px',
              }
            : undefined
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium">
              <CheckCircle className="w-3 h-3" />
              {txt(badge, 'A slice of love')}
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold leading-tight text-foreground">
              {txt(titlePrefix, 'Sabor que')} <span className="text-primary">{txt(titleHighlight, 'conquista')}</span>
            </h2>
            <p className="text-muted-foreground text-sm">
              {txt(subtitle, 'Pizza recién hecha, ingredientes frescos y entrega a domicilio.')}
            </p>
            <div className="flex gap-2 pointer-events-none opacity-90">
              <Button size="sm" className="rounded-full text-xs gap-1" type="button" tabIndex={-1}>
                {txt(ctaPrimaryLabel, 'Ordenar ahora')}
                <ArrowRight className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs" type="button" tabIndex={-1}>
                {txt(ctaSecondaryLabel, 'Ver menú')}
              </Button>
            </div>
          </div>
          <div className="relative">
            <img src={heroImg} alt={brand} className="rounded-2xl w-full h-36 md:h-44 object-cover shadow-md" />
          </div>
        </div>
      </div>
    </PreviewFrame>
  );
}

export function FeaturesPreview({ featureRows = [] }) {
  const features =
    Array.isArray(featureRows) && featureRows.some((r) => String(r.title || '').trim())
      ? featureRows.filter((r) => String(r.title || '').trim() || String(r.desc || '').trim())
      : DEFAULT_HOME_FEATURES;
  return (
    <PreviewFrame label="Vista previa — tarjetas de valores">
      <div className="grid grid-cols-2 gap-3">
        {features.map((feature, index) => {
          const Cmp = iconFromApiName(feature.icon);
          return (
            <Card key={index} className="border border-border p-3 text-center">
              <CardContent className="p-0 space-y-2">
                <div className="w-9 h-9 mx-auto bg-primary/10 rounded-lg flex items-center justify-center">
                  <Cmp className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-xs text-foreground">{txt(feature.title, '—')}</h3>
                <p className="text-[10px] text-muted-foreground leading-snug">{txt(feature.desc, '—')}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </PreviewFrame>
  );
}

export function ServicesSectionPreview({ title, intro, bulletsText = '', imageUrl, brandName }) {
  const brand = txt(brandName, DEFAULT_BRAND_NAME);
  const bullets = String(bulletsText || '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const items = bullets.length > 0 ? bullets : DEFAULT_SERVICES_BULLETS;
  const img = resolveImg(imageUrl, DEFAULT_CLINIC_IMAGE);
  return (
    <PreviewFrame label="Vista previa — sección «¿Por qué elegirnos?»">
      <div className="rounded-xl bg-muted/30 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground mb-2">
              {txt(title, '¿Por qué elegirnos?')}
            </h2>
            <p className="text-muted-foreground text-xs mb-3">
              {txt(intro, 'Calidad, rapidez y el sabor que buscas en cada rebanada.')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items.map((service, index) => (
                <div
                  key={`${index}-${service}`}
                  className="flex items-center gap-2 bg-white p-2 rounded-lg border border-border text-xs"
                >
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{service}</span>
                </div>
              ))}
            </div>
          </div>
          <img src={img} alt={brand} className="rounded-2xl w-full h-36 object-cover" />
        </div>
      </div>
    </PreviewFrame>
  );
}

export function LowerSectionsPreview({
  featuredProductsTitle,
  featuredProductsIntro,
  reviewsSectionTitle,
  ctaTitle,
  ctaSubtitle,
  ctaButtonLabel,
}) {
  return (
    <PreviewFrame label="Vista previa — secciones inferiores del home">
      <div className="space-y-4 text-sm">
        <div className="text-center p-3 bg-white rounded-lg border border-border">
          <h3 className="font-heading font-bold text-base mb-1 text-foreground">
            {txt(featuredProductsTitle, 'Del menú')}
          </h3>
          <p className="text-muted-foreground text-xs">
            {txt(featuredProductsIntro, 'Algunas de nuestras pizzas más pedidas')}
          </p>
          <p className="text-[10px] text-muted-foreground mt-2 italic">
            Las tarjetas de pizza se cargan desde el menú (Admin → Menú).
          </p>
        </div>
        <div className="text-center p-3 bg-muted/30 rounded-lg border border-border">
          <h3 className="font-heading font-bold text-base text-foreground">
            {txt(reviewsSectionTitle, 'Lo que dicen nuestros clientes')}
          </h3>
          <p className="text-[10px] text-muted-foreground mt-2 italic">
            Las opiniones aprobadas aparecen aquí (Admin → Reseñas).
          </p>
        </div>
        <div className="text-center p-4 bg-primary rounded-lg text-primary-foreground">
          <h3 className="font-heading font-bold text-base mb-1">{txt(ctaTitle, '¿Antojo de pizza?')}</h3>
          <p className="text-primary-foreground/80 text-xs mb-3">
            {txt(ctaSubtitle, 'Ordena en minutos. Te avisamos cuando va en camino.')}
          </p>
          <Button
            size="sm"
            variant="secondary"
            className="rounded-full text-xs bg-white text-primary pointer-events-none"
            type="button"
            tabIndex={-1}
          >
            {txt(ctaButtonLabel, 'Pide tu pizza')}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </PreviewFrame>
  );
}
